import { Signal, createEffect, createSignal, onCleanup } from "solid-js";
import { SetStoreFunction, Store, createStore, produce } from "solid-js/store";

/**
 * Syncronize a signal across multiple windows.
 * @param channelName A unique name for this signal
 * @param data The default value if the signal hasn't been initialized, yet
 * @returns A Signal<T> object that can be used as a normal signal.
 */
export function createSharedSignal<T>(channelName: string, data: () => T): Signal<T> {
        channelName = `ST_SESSION_SHARED_${channelName}`;
    
        const bc = new BroadcastChannel(channelName);
        const [value, setValue] = createSignal(data());
    
        if (!localStorage[channelName]) {
            localStorage[channelName] = JSON.stringify(value());
        } else {
            console.log('restoring', channelName, localStorage[channelName])
            setValue(JSON.parse(localStorage[channelName]));
        }
    
        createEffect(() => {
            const storedJson = localStorage[channelName];
            const currentJson = JSON.stringify(value());
            
            if (storedJson !== currentJson) {
                localStorage[channelName] = currentJson;
            }
            bc.postMessage(currentJson);
        })
    
        bc.onmessage = (msg) => {
            const currentJson = JSON.stringify(value());
    
            if (currentJson !== msg.data) {
                localStorage[channelName] = msg.data;
                requestAnimationFrame(() => setValue(JSON.parse(msg.data)));
            }
        }
    
        onCleanup(() => bc.close());
    
        return [value, setValue];
}

/**
 * Syncronize a store across multiple windows.
 * @param channelName A unique name for this store
 * @param data The default value if the store hasn't been initialized, yet
 * @returns A Store<> object that can be used as a normal store.
 */
export function createSharedStore<T extends object>(channelName: string, data: T): [get: Store<T>, set: SetStoreFunction<T>] {
    channelName = `ST_SESSION_SHARED_${channelName}`;

    const bc = new BroadcastChannel(channelName);
    const [store, setStore] = createStore(data);

    // restore the current setting
    if (!localStorage[channelName]) {
        localStorage[channelName] = JSON.stringify(data);
    }
    else {
        merge(JSON.parse(localStorage[channelName]));
    }

    function merge(data: T) {
        setStore(produce(state => {
            Object.assign(state, data);
        }));
    }

    createEffect(() => {
        const currJson = localStorage[channelName];
        const newJson = JSON.stringify(store);

        if (currJson !== newJson) {
            localStorage[channelName] = newJson;
        }
        bc.postMessage(newJson);
    });

    bc.onmessage = (msg) => {
        const currJson = JSON.stringify(store);

        if (currJson !== msg.data) {
            merge(JSON.parse(msg.data) as T);
        }
    }

    onCleanup(() => {
        bc.close();
    });

    return [store, setStore];
}

/**
 * A variation of createSharedStore that allows calling a method across all open windows
 * @param name A unique name for this callback
 * @param callback The method to execute when the event is triggered
 * @returns A dispatch method that will trigger the event
 */
export function createSyncronizedCallback<T extends (...args: any[]) => any>(name: string, callback: T): T {
    const channelName = `ST_SESSION_Sync_${name}`;
    const bc = new BroadcastChannel(channelName);

    bc.onmessage = (msg) => {
        callback.apply(window, msg.data);
    };

    return function dispatch(...args: Parameters<T>) {
        callback.apply(window, args);
        bc.postMessage(args);
    } as T;
}
